import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/etfs.png';
import { Layers, TrendingUp, ChartPie, TrendingUpDown, ChartCandlestick, Globe } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/allocation-card');
const W1 = React.lazy(loadW1);
const loadW11 = () => import('@/components/landing/product/main/datasets/expense-ratio-card');
const W11 = React.lazy(loadW11);
const loadW111 = () => import('@/components/landing/product/main/datasets/diversification-card');
const W111 = React.lazy(loadW111);
const loadW2 = () => import('@/components/landing/product/main/datasets/etf-performance-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/market-breadth-vehicle-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/stock-market-pulse-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/rebalance-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const StocksPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW11, loadW111, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="ETF Data API: Holdings, Flows & Fund Analytics"
        description="ETF data API with daily holdings, fund flows, NAV, expense ratios, and factor exposures for 10,000+ funds globally. Daily and hourly liquidity analytics."
        canonicalUrl="https://axionquant.com/products/etfs"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion ETF Data & Intelligence API",
          "description": "Comprehensive ETF data with daily holdings, fund flows, NAV, expense ratios, and factor exposures for over 10,000 funds across global markets. Daily and hourly liquidity and institutional analytics.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "ETF Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
  docs="/docs/01-market-data/ETFs"
  title="ETF Data & Intelligence, Unpacked"
  body="Go beyond the ticker. Access fund-level details, constituent holdings, sector allocations, and geographic exposure for ETFs across global markets - so you can model the true underlying risk of any fund."
  crumb={{name: 'ETFs', path:'/products/etfs'}}
  tabs={[
    {
      label: 'Fund Comparison',
      language: 'python',
      filename: 'fund_comparison.py',
      bg: '6.jpeg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

etfs = ["SPY", "QQQ", "VTI", "IWM", "DIA"]

def fetch_fund(row):
    t    = row["ticker"]
    fund = client.etfs.fund(t)
    return (t, {
        "ticker":        t,
        "name":          fund["longName"],
        "aum":           fund["totalAssets"],
        "expense_ratio": fund["expenseRatio"],
        "category":      fund["category"],
    })

df_etfs   = utils.df([{"ticker": t} for t in etfs])
fund_map  = {}
utils.work(df_etfs, fetch_fund, fund_map)

df_funds  = utils.df(list(fund_map.values()))
df_funds  = df_funds.sort_values("aum", ascending=False)

visualize.bar(df_funds, x="ticker", y="aum")
`,
    },
    {
      label: 'Holdings Overlap',
      language: 'python',
      filename: 'holdings_overlap.py',
      bg: '5.jpeg',
      code: `from axion import Axion, utils

client = Axion(api_key="your_api_key")

etfs = ["SPY", "QQQ", "VTI"]

holdings = {
    t: utils.df(client.etfs.holdings(t))
    for t in etfs
}

overlap = utils.overlap(
    [holdings[t] for t in etfs],
    col="symbol",
)

unique = {
    t: utils.difference(
        [holdings[t], *[holdings[o] for o in etfs if o != t]],
        col="symbol",
    )
    for t in etfs
}

top_shared = (
    holdings["SPY"][holdings["SPY"]["symbol"].isin(overlap)]
    .sort_values("holdingPercent", ascending=False)
    .head(10)
)
`,
    },
    {
      label: 'Sector Exposure',
      language: 'python',
      filename: 'sector_exposure.py',
      bg: '7.jpeg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

etfs = ["SPY", "QQQ", "IWM"]

def fetch_exposure(row):
    t        = row["ticker"]
    exposure = client.etfs.exposure(t)
    return (t, exposure)

df_etfs      = utils.df([{"ticker": t} for t in etfs])
exposure_map = {}
utils.work(df_etfs, fetch_exposure, exposure_map)

for ticker, exposure in exposure_map.items():
    df_exp = utils.df(exposure)
    visualize.pie(
        df_exp,
        values="expPercent",
        labels="sector",
    )
`,
    },
    {
      label: 'Concentration Risk',
      language: 'python',
      filename: 'concentration_risk.py',
      bg: '8.jpeg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

etfs = ["QQQ", "SPY", "VTI", "IWM"]

def fetch_concentration(row):
    t        = row["ticker"]
    holdings = client.etfs.holdings(t)
    df_h     = utils.df(holdings)
    top10    = df_h.nlargest(10, "holdingPercent")["holdingPercent"].sum()
    top1     = df_h["holdingPercent"].max()
    return (t, {
        "ticker":      t,
        "top10_weight": top10,
        "top1_weight":  top1,
        "n_holdings":   len(df_h),
    })

df_etfs   = utils.df([{"ticker": t} for t in etfs])
conc_map  = {}
utils.work(df_etfs, fetch_concentration, conc_map)

df_conc = utils.df(list(conc_map.values()))

visualize.scatter(
    df_conc,
    x="n_holdings",
    y="top10_weight",
    hover=["ticker", "top1_weight"],
)
`,
    },
  ]}
/>
      <Marquee/>

     <FeaturesGrid
        title="ETF Intelligence. "
        subtitle="Holdings, fund flows, and NAV analytics. "
        features={[
        {
          fig: 'FIG 0.1',
          icon: <Layers size={80} color="var(--purple)" />,
          title: 'Daily Holdings & Allocations',
          description: 'Full position-level transparency with daily updates. See exactly what every ETF holds, from top 10 constituents to complete portfolio breakdowns.',
        },
        {
          fig: 'FIG 0.2',
          icon: <TrendingUp size={80} color="var(--green)" />,
          title: 'Fund Flow Analysis',
          description: 'Track daily creations and redemptions across 10,000+ ETFs. Identify institutional money moving in and out before it impacts price.',
        },
        {
          fig: 'FIG 0.3',
          icon: <TrendingUpDown size={80} color="var(--red)" />,
          title: 'NAV & Premium/Discount',
          description: 'Monitor historical and end-of-day NAV vs. market price. Analyze premium/discount spreads over time to spot mispricings and assess execution quality.',
        },
        {
          fig: 'FIG 0.4',
          icon: <ChartPie size={80} color="var(--purple)" />,
          title: 'Factor & ESG Decomposition',
          description: 'Deconstruct any fund into value, growth, momentum, quality, and ESG exposures. Model style drift, overlap, and unintended bets across your portfolio.',
        },
        {
          fig: 'FIG 0.5',
          icon: <ChartCandlestick size={80} color="var(--green)" />,
          title: 'Expense & Cost Analysis',
          description: 'Compare expense ratios, liquidity costs, and tracking difference. Full cost-of-ownership metrics for smarter fund selection and benchmarking.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global Fund Coverage',
          description: 'ETFs from 50+ markets covering equity, fixed income, commodity, and thematic strategies. One API across the entire global fund universe.',
        },
      ]} />


      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Global Coverage"
        headingPlain="ETF Intelligence Across"
        headingEm="50+ Markets"
        body="Track over 10,000 funds worldwide with daily holdings, fund flows, NAV, expense ratios, and factor exposures. Optimized for long-horizon research with daily and hourly data."
        stats={[
          { v: "10,000+", l: "ETFs Tracked" },
          { v: "$8T+", l: "AUM Coverage" },
          { v: "Daily", l: "Holdings Updates" },
          { v: "15+ Years", l: "Historical Data" },
        ]}
        markers={[
          // North America - Exchanges & Issuers
          { id: "nyse-arca", location: [40.71, -74.01], label: "NYSE Arca (Primary ETF Exchange)" },
          { id: "nasdaq", location: [40.76, -73.99], label: "Nasdaq (Tech & Thematic ETFs)" },
          { id: "cboe", location: [41.88, -87.63], label: "Cboe Global Markets (ETF Listings)" },
          { id: "blackrock", location: [40.75, -73.97], label: "BlackRock / iShares (Largest ETF Issuer)" },
          { id: "vanguard", location: [40.08, -75.65], label: "Vanguard (Low-Cost ETF Leader)" },
          { id: "state-street", location: [42.36, -71.06], label: "State Street / SPDR (ETF Pioneer)" },
          { id: "invesco", location: [33.75, -84.39], label: "Invesco / QQQ (Nasdaq-100 ETF)" },
          { id: "schwab", location: [38.58, -121.50], label: "Charles Schwab ETFs" },
          { id: "jp-morgan-am", location: [40.71, -74.00], label: "J.P. Morgan Asset Management ETFs" },
          { id: "fidelity", location: [42.36, -71.05], label: "Fidelity ETFs" },
          { id: "proshares", location: [39.95, -75.16], label: "ProShares (Leveraged & Thematic ETFs)" },
          { id: "direxion", location: [40.75, -73.98], label: "Direxion (Leveraged ETFs)" },
          { id: "ark-invest", location: [27.95, -82.45], label: "ARK Invest (Innovation ETFs)" },
          { id: "global-x", location: [40.75, -73.99], label: "Global X ETFs (Thematic ETFs)" },
          { id: "first-trust", location: [41.88, -87.63], label: "First Trust ETFs" },
          { id: "pimco", location: [33.86, -118.25], label: "PIMCO ETFs (Fixed Income)" },
          { id: "dimensional", location: [30.27, -97.74], label: "Dimensional ETFs" },
          { id: "wisdomtree-us", location: [40.75, -73.98], label: "WisdomTree US ETFs" },

          // Canada
          { id: "tsx", location: [43.65, -79.38], label: "Toronto Stock Exchange (ETF Listings)" },
          { id: "purpose", location: [43.65, -79.38], label: "Purpose Investments ETFs" },
          { id: "horizons", location: [43.65, -79.38], label: "Horizons ETFs Canada" },
          { id: "bmo-etf", location: [43.65, -79.38], label: "BMO ETFs" },
          { id: "ci-financial", location: [43.65, -79.38], label: "CI Global Asset Management ETFs" },

          // Europe
          { id: "lse", location: [51.51, -0.10], label: "London Stock Exchange (UCITS ETF Hub)" },
          { id: "deutsche-boerse", location: [50.11, 8.68], label: "Deutsche Börse / Xetra ETFs" },
          { id: "euronext", location: [48.89, 2.36], label: "Euronext ETFs" },
          { id: "six", location: [47.37, 8.54], label: "SIX Swiss Exchange ETFs" },
          { id: "amundi", location: [48.87, 2.30], label: "Amundi ETFs" },
          { id: "lyxor", location: [48.87, 2.33], label: "Lyxor ETFs" },
          { id: "dws-xtrackers", location: [50.11, 8.68], label: "DWS Xtrackers ETFs" },
          { id: "ubs-etf", location: [47.37, 8.54], label: "UBS ETFs" },
          { id: "credit-suisse", location: [47.37, 8.54], label: "Credit Suisse ETFs" },
          { id: "legal-general", location: [51.51, -0.12], label: "Legal & General Investment ETFs" },
          { id: "invesco-europe", location: [51.51, -0.13], label: "Invesco Europe ETFs" },
          { id: "ishares-europe", location: [51.51, -0.10], label: "iShares Europe UCITS ETFs" },

          // Asia-Pacific
          { id: "hkex", location: [22.28, 114.16], label: "HKEX ETFs" },
          { id: "tse", location: [35.68, 139.77], label: "Tokyo Stock Exchange ETFs" },
          { id: "asx", location: [-33.86, 151.21], label: "ASX ETFs" },
          { id: "sse", location: [31.23, 121.47], label: "Shanghai ETFs" },
          { id: "sgx", location: [1.28, 103.85], label: "Singapore Exchange ETFs" },
          { id: "kospi", location: [37.57, 126.98], label: "Korea Exchange ETFs" },
          { id: "nse-india", location: [19.07, 72.87], label: "National Stock Exchange India ETFs" },
          { id: "bse-india", location: [18.93, 72.83], label: "Bombay Stock Exchange ETFs" },
          { id: "taiwan", location: [25.03, 121.56], label: "Taiwan ETF Market" },
          { id: "nzx", location: [-36.85, 174.76], label: "New Zealand Exchange ETFs" },

          // Middle East & Emerging Markets
          { id: "saudi-tadawul", location: [24.71, 46.67], label: "Saudi Exchange ETFs" },
          { id: "dubai-financial", location: [25.20, 55.27], label: "Dubai Financial Market ETFs" },
          { id: "johannesburg", location: [-26.20, 28.04], label: "Johannesburg Stock Exchange ETFs" },
          { id: "brazil-b3", location: [-23.55, -46.63], label: "B3 Brazil ETFs" },
          { id: "mexico-bmv", location: [19.43, -99.13], label: "Mexican ETF Market" },

          // Index Providers & ETF Benchmarks
          { id: "sp-global", location: [40.71, -74.01], label: "S&P Global ETF Indexes" },
          { id: "msci", location: [40.75, -73.98], label: "MSCI Global ETF Benchmarks" },
          { id: "ftse-russell", location: [51.51, -0.09], label: "FTSE Russell ETF Indexes" },
          { id: "nasdaq-indexes", location: [40.76, -73.99], label: "Nasdaq Index ETF Benchmarks" },
          { id: "solactive", location: [50.11, 8.68], label: "Solactive ETF Index Provider" },
          { id: "stoxx", location: [50.11, 8.68], label: "STOXX ETF Indexes" },
        ]}
      />

      <DataGrid
        title="ETF & Funds Intelligence, Unpacked"
        subtitle="Go beyond the ticker. Analyze holdings, flows, liquidity, premiums/discounts, and factor exposures. Model the true underlying risk and opportunity of any fund."
        bg={databg}
        stats={[
          { label: 'ETF/ETP Tickers', value: '10,000+' },
          { label: 'Daily Holdings Updates', value: '100%' },
          { label: 'Markets Covered', value: '50+' },
        ]}
      />
      <CardList
        title="Deconstruct Every Fund"
        vid={'https://cdn.studio14a.com/axion/products/cards/etfs.mp4'}
        points={[
          {
            "title": "Daily Holdings & Exposure Analysis",
            "details": "Access full, daily-updated portfolio holdings for ETFs, mutual funds, and indices. Calculate sector, country, and single-stock exposures to see what you're really buying.",
            "cta": "Explore holdings data",
            "url": "/docs"
          },
          {
            "title": "Flows & Liquidity Metrics",
            "details": "Monitor daily creation/redemption activity, spread costs, volume, and premium/discount to NAV. Identify institutional moves and liquidity risk before it impacts price.",
            "cta": "View liquidity analytics",
            "url": "/products/stocks"
          },
          {
            "title": "Factor & ESG Decomposition",
            "details": "Deconstruct any fund into its underlying factor exposures (value, growth, momentum, volatility) and ESG characteristics. Model portfolio overlap and unintended bets.",
            "cta": "Analyze fund factors",
            "url": "/docs"
          }
        ]}
      />

      <Responses
        heading="ETF Intelligence, Unpacked"
        subheading="Daily holdings, sector exposures, fund ratings, and liquidity metrics for over 10,000 global ETFs"
        ticker="SPY"
        tabs={[
          {
            title: "Fund Overview",
            description: "Fund name, asset class, category, focus, and primary exchange",
            data: {
              "id": 97,
              "name": "State Street SPDR S&P 500 ETF",
              "ticker": "SPY",
              "symbol": "AMEX:SPY",
              "exchange": "AMEX",
              "market": "america",
              "country": "United States",
              "type": "etf fund",
              "sector": "Miscellaneous",
              "industry": "Investment Trusts/Mutual Funds",
              "currency": "USD",
              "lastClose": 752.82000732,
              "changePct": -0.282138533088403,
              "issuer": "State Street Investment Management",
              "description": "The trust seeks to achieve its investment objective by holding a portfolio of the common stocks that are included in the index (the Portfolio), with the weight of each stock in the Portfolio substantially corresponding to the weight of such stock in the index.",
              "inception": "1993-01-22T00:00:00.000Z"
            },
          },
          {
            title: "Top Holdings",
            description: "Current top holdings with weights, shares, and market value",
            data: [
              {
                "ticker": "NVDA",
                "name": "NVIDIA Corporation",
                "weight": "7.17%",
                "shares": 305156465,
                "marketValue": 44519276678.85,
                "rawWeight": 0.0716544211188534
              },
              {
                "ticker": "AAPL",
                "name": "Apple Inc.",
                "weight": "6.95%",
                "shares": 188603226,
                "marketValue": 43190138754,
                "rawWeight": 0.0695151543630329
              },
              {
                "ticker": "MSFT",
                "name": "Microsoft Corporation",
                "weight": "6.17%",
                "shares": 92204348,
                "marketValue": 38309984550.52,
                "rawWeight": 0.0616604754349895
              },
              {
                "ticker": "AMZN",
                "name": "Amazon.com, Inc.",
                "weight": "3.78%",
                "shares": 115879530,
                "marketValue": 23509639046.4,
                "rawWeight": 0.0378391048159882
              },
              {
                "ticker": "META",
                "name": "Meta Platforms Inc Class A",
                "weight": "2.47%",
                "shares": 27101942,
                "marketValue": 15326690239.84,
                "rawWeight": 0.0246685301004779
              }]
          },
          {
            title: "Sector Weights",
            description: "Sector allocation breakdown with raw and formatted weights",
            data: {
              "sector": [
                {
                  "name": "Technology Services",
                  "weight": "21.08%",
                  "rawWeight": 0.21079285342758
                },
                {
                  "name": "Electronic Technology",
                  "weight": "18.37%",
                  "rawWeight": 0.183720089894102
                },

              ],
              "regions": [
                {
                  "name": "United States",
                  "weight": "100.00%",
                  "benchmarkWeight": "99.89%",
                  "rawWeight": 1
                },
                {
                  "name": "Canada",
                  "weight": "--",
                  "benchmarkWeight": "0.11%",
                  "rawWeight": "--"
                }
              ]
            },
          },
          {
            title: "Component Exposure",
            description: "Find all the ETFs a corporate ticker is included in",
            data: [
              {
                "ticker": "XLK",
                "fundName": "Technology Select Sector SPDR Fund",
                "segment": "Equity: U.S. Information Technology",
                "allocation": "24.17%",
                "marketValue": "$10.46B",
                "rawAllocation": 0.2417,
                "rawMarketValue": 10460000000
              },
              {
                "ticker": "FTEC",
                "fundName": "Fidelity MSCI Information Technology Index ETF",
                "segment": "Equity: U.S. Information Technology",
                "allocation": "23.75%",
                "marketValue": "$1.45B",
                "rawAllocation": 0.2375,
                "rawMarketValue": 1450000000
              },
              {
                "ticker": "IXN",
                "fundName": "iShares Global Tech ETF",
                "segment": "Equity: Global Information Technology",
                "allocation": "23.39%",
                "marketValue": "$737.66M",
                "rawAllocation": 0.2339,
                "rawMarketValue": 737660000
              }],

          },
          {
            title: 'Latest Quote',
            description: 'Latest quote for an ETF including latest price, NAV, EMAs, 52-week range, and YTD performance',
            data: {
              "latestClose": 752.82000732,
              "latestOpen": 752.4699707,
              "latestHigh": 753.91052246,
              "latestLow": 750.31500244,
              "previousClose": 754.95001221,
              "week52Range": {
                "high": 760.4,
                "low": 618.05
              },
              "week52AvgClose": 684.1554,
              "ema30": 743.0846,
              "ema60": 732.4742,
              "ema90": 722.6317,
              "ema120": 714.1594,
              "ytdClosePctChange": 10.1951,
              "volume": 11036221,
              "avgVolumeLifetime": 83094224.5303,
              "nav": 751.69257000,
              "expense": 0.0009450000
            },
          },
          {
            title: 'All Tickers',
            description: 'List of all available ETFs with exchange, market, and type classification',
            data: [
              { id: 1, ticker: "SPY", symbol: "AMEX:SPY", exchange: "AMEX", market: "america", type: "etf fund" },
              { id: 2, ticker: "QQQ", symbol: "NASDAQ:QQQ", exchange: "NASDAQ", market: "america", type: "etf fund" },
              { id: 3, ticker: "IWM", symbol: "NYSEARCA:IWM", exchange: "NYSEARCA", market: "america", type: "etf fund" },
              { id: 4, ticker: "EFA", symbol: "NYSEARCA:EFA", exchange: "NYSEARCA", market: "america", type: "etf fund" },
              { id: 5, ticker: "VTI", symbol: "NYSEARCA:VTI", exchange: "NYSEARCA", market: "america", type: "etf fund" },
            ],
          },
          {
            title: 'Historical Prices',
            description: 'Daily OHLCV price data for an ETF ticker with volume',
            data: [
              {
                "time": "2026-06-23T00:00:00.000Z",
                "ticker": "SPY",
                "open": "733.80999756",
                "high": "739.63000488",
                "low": "732.29998779",
                "close": "735.53997803",
                "volume": "34120629"
              },
              {
                "time": "2026-06-22T00:00:00.000Z",
                "ticker": "SPY",
                "open": "747.70001221",
                "high": "750.17999268",
                "low": "743.13000488",
                "close": "744.39001465",
                "volume": "46204600"
              },
              {
                "time": "2026-06-18T00:00:00.000Z",
                "ticker": "SPY",
                "open": "747.76000977",
                "high": "748.22998047",
                "low": "743.85998535",
                "close": "746.73999023",
                "volume": "80875700"
              },
            ]
          },
        ]}
      />
      <DatasetDetails full={true}
        title="Comprehensive Equity Market Coverage"
        subtitle="Access daily and hourly market data, historical prices dating back to 1987, and complete corporate action information for over 100,000 stocks across major global exchanges. Power quantitative trading strategies, risk management systems, and portfolio analytics with institutional-grade data."
        cta="Start For Free"
        ctaLink="/login"
        tiles={[
          {
            img: '7.webp',
            title: 'We cover over 100,000 symbols',
            caption: 'You can access data for stocks and ETFs across every major global market.',
            cn: 'scaled',
            tile: (<div>
                  <W1/>
                  <W11/>
                  <W111/>
                </div>)
          },
          {
            img: '0.webp',
            title: 'We connect to 85+ major exchanges',
            caption: 'You get comprehensive coverage of global trading venues with daily and hourly data.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '13.webp',
            title: 'We provide 400B+ data points',
            caption: 'You can perform institutional-grade analysis with one of the largest financial datasets available.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '2.webp',
            title: 'We offer 35+ years of history',
            caption: 'You can backtest strategies across decades of market data for robust historical analysis.',
            cn: 'scaled corner',
            tile: <W2/>
          },
          {
            img: '10.webp',
            title: 'We retrieve data in milliseconds',
            caption: 'You can pull decades of history nearly instantly to power long-horizon research and backtesting.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Historical ETF intelligence you can rely on."
        leftCell={{
          stat: '15+',
          title: 'Years of historical data.',
          desc: 'Backtest, model factor exposures, and analyze full market cycles with daily holdings history.',
          showChart: true,
        }}
        rightCells={[
          {
            stat: 'Daily',
            title: 'Holdings updates.',
            desc: 'Every fund refreshed every business day for accurate, position-level transparency.',
          },
          {
            stat: '99.9%',
            title: 'Data completeness.',
            desc: 'No gaps across 10,000+ ETFs and $8T in AUM.',
            boldDesc: true,
          },
        ]}
      /><ProductFAQ slug="etfs" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default StocksPage;

